import { formatDate } from "@/lib/format";
// web/src/pages/clients/components/ClientJobsTab.tsx
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Briefcase } from "lucide-react";
import { jobsApi, type JobListItemDto } from "@/lib/jobs";
import { JobStatusBadge } from "@/components/job-status-badge";
import { EntityEmptyStub } from "@/components/detail/EntityEmptyStub";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

const PAGE_SIZE = 10;

export interface ClientJobsTabProps {
  clientId: number;
}

/**
 * Inline mini-list of the most recent jobs for a client, displayed on
 * ClientDetailPage's Jobs tab. The "View all" link deep-links into the
 * JobsListPage with `?clientId=N` applied.
 */
export function ClientJobsTab({ clientId }: ClientJobsTabProps) {
  const query = useQuery({
    queryKey: ["client-jobs", clientId, PAGE_SIZE],
    queryFn: () => jobsApi.list({ clientId, page: 0, pageSize: PAGE_SIZE }),
    enabled: clientId > 0,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  const rows = query.data?.items ?? [];

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <EntityEmptyStub
          icon={Briefcase}
          title="No jobs for this client yet"
          helper="Jobs raised for this client will appear here."
        />
        <div className="text-right">
          <Link to={`/jobs?clientId=${clientId}`} className="text-sm text-primary underline">
            View all jobs for this client →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Job Ref</th>
              <th className="px-3 py-2">Status entered</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Owner</th>
              <th className="px-3 py-2 text-right">Total inc GST</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: JobListItemDto) => (
              <tr key={r.jobId} className="border-t border-border">
                <td className="px-3 py-2">
                  <Link to={`/jobs/${r.jobId}`} className="text-primary underline">
                    {r.jobReferenceNo}
                  </Link>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatDate(r.currentStatusEnteredUtc)}
                </td>
                <td className="px-3 py-2">
                  <JobStatusBadge code={r.currentStatusCode} color={r.statusColor} />
                </td>
                <td className="px-3 py-2">{r.ownerEmployeeName ?? "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.totalIncGst != null ? fmt.format(r.totalIncGst) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-right">
        <Link to={`/jobs?clientId=${clientId}`} className="text-sm text-primary underline">
          View all jobs for this client →
        </Link>
      </div>
    </div>
  );
}
